import { Box } from '@mantine/core';
import type { ReactNode } from 'react';

import { PageFrame } from '../ui/PageFrame';
import { SiteFooter } from '../ui/SiteFooter';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { ClosingBanner } from './ClosingBanner';
import { EmailVerificationBanner } from './EmailVerificationBanner';
import { PlatformBanner } from './PlatformBanner';

/**
 * AppShell — wraps every authenticated app surface with a fixed-width LHS
 * navigation sidebar (lg+), a slimmed top header band, and the page content
 * inside a canonical `<PageFrame>` primitive.
 *
 * Layout on lg+ (the productivity-app pattern):
 *
 *   ┌─ AppSidebar (240px, sticky) ─┬─ AppHeader (sticky) ─────────┐
 *   │                              │ logo … bell  avatar          │
 *   │                              ├──────────────────────────────┤
 *   │  brand + hat switcher        │                              │
 *   │  Dashboard / Inbox           │  PageFrame → content         │
 *   │  Browse / Events             │                              │
 *   │  hat-specific section        │                              │
 *   │                              ├──────────────────────────────┤
 *   │                              │ SiteFooter                   │
 *   └──────────────────────────────┴──────────────────────────────┘
 *
 * Below lg the sidebar collapses; AppHeader's burger drawer surfaces the
 * same navigation in a sheet. The header keeps the brand mark, notifications
 * bell, and avatar/UserMenu so identity and quick actions stay reachable.
 *
 * Tones (passed through to PageFrame):
 *   - content (default) — 1280px content max, edges align with the AppHeader.
 *   - form              — 720px content max, for settings / single forms.
 *   - full              — no max, page owns its own layout (inbox + dashboards).
 *
 * `compact` drops vertical padding from `xl` → `lg` for surfaces whose
 * internal rhythm is already dense (e.g. inbox).
 */
const SIDEBAR_WIDTH = 240;
// AppHeader height: the 64px sticky row + the 3px brand accent line beneath it.
// The sticky sidebar sits just under it.
const HEADER_HEIGHT = 67;

export function AppShell({
  children,
  tone = 'content',
  compact = false,
}: {
  children: ReactNode;
  tone?: 'content' | 'form' | 'full';
  compact?: boolean;
}) {
  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Full-width sticky top band: brand + primary nav tabs + identity. */}
      <AppHeader />

      <Box style={{ display: 'flex', alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        <Box
          visibleFrom="lg"
          style={{
            flex: `0 0 ${SIDEBAR_WIDTH}px`,
            alignSelf: 'flex-start',
            position: 'sticky',
            top: HEADER_HEIGHT,
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
            borderRight: '1px solid light-dark(color-mix(in srgb, var(--wwaave-brand-primary) 10%, transparent), var(--mantine-color-dark-5))',
            background: 'light-dark(var(--wwaave-body-bg), var(--mantine-color-dark-7))',
            zIndex: 30,
          }}
        >
          {/* Brand lives in the top nav bar on lg+, so the rail omits it. */}
          <AppSidebar showBrand={false} />
        </Box>

        <Box
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            background: 'light-dark(var(--wwaave-body-bg), var(--mantine-color-dark-8))',
          }}
        >
          <PlatformBanner />
          <ClosingBanner />
          <EmailVerificationBanner />
          <Box component="main" style={{ flex: 1 }}>
            <PageFrame tone={tone} compact={compact}>
              {children}
            </PageFrame>
          </Box>
          <SiteFooter variant="app" />
        </Box>
      </Box>
    </Box>
  );
}
